// =============================================================================
// FILE: packages/renderer/src/components/dashboard/DualWorldClock.tsx
//
// ARCHITECTURAL RULE
//   PURE UI consumer of TimezoneService + ProjectContext.  Reads zero
//   timezone state on its own; performs zero timezone math outside the
//   service. All formatting goes through TimezoneService.formatTimeInZone.
//
//   When the user changes the active project, the Client clock updates
//   reactively because ProjectContext re-resolves the clientTimezone.
//
// INTERACTIVE TOGGLE
//   Clicking the time display on either clock toggles between 12-hour
//   and 24-hour format. Preference persisted to localStorage under the
//   key `rcd2-clock-format` ('12h' | '24h'). Defaults to 12-hour.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Clock, Briefcase, MapPin, AlertTriangle, RefreshCw } from 'lucide-react';
import { TimezoneService } from '../../services/TimezoneService.js';
import { useActiveProject } from '../../stores/projectContext.js';

const STORAGE_KEY = 'rcd2-clock-format';

/** Reads/writes the clock format preference from localStorage. */
function useClockFormat(): { is12h: boolean; toggle: () => void } {
  const [is12h, setIs12h] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== '24h';
    } catch {
      return true; // default: 12-hour
    }
  });

  const toggle = useCallback(() => {
    setIs12h((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? '12h' : '24h'); } catch { /* noop */ }
      window.dispatchEvent(new CustomEvent('r2:clock-format', { detail: { is12h: next } }));
      return next;
    });
  }, []);

  // React to Emmy System Control (or any other surface) changing the format.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ is12h: boolean }>).detail;
      if (detail && typeof detail.is12h === 'boolean') setIs12h(detail.is12h);
    };
    window.addEventListener('r2:clock-format', handler);
    return () => window.removeEventListener('r2:clock-format', handler);
  }, []);

  return { is12h, toggle };
}

export function DualWorldClock(): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const { is12h, toggle } = useClockFormat();
  const active  = useActiveProject();
  const localTz = TimezoneService.getLocalTimezone();

  const clientOutside = useMemo(
    () => TimezoneService.isOutsideBusinessHours(active.clientTimezone, now),
    [active.clientTimezone, now],
  );

  return (
    <section
      data-testid="dual-world-clock"
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4"
    >
      {/* Format badge — shared indicator */}
      <div className="sm:col-span-2 flex items-center justify-end gap-2 -mb-1">
        <button
          type="button"
          data-testid="clock-format-toggle"
          onClick={toggle}
          title={`Switch to ${is12h ? '24-hour' : '12-hour'} format`}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-zinc-700 text-zinc-500 hover:text-cyan-300 hover:border-cyan-400/40 transition text-[9.5px] uppercase tracking-[0.2em]"
        >
          <RefreshCw className="h-2.5 w-2.5" />
          {is12h ? '12h AM/PM' : '24h'}
        </button>
      </div>

      <ClockCard
        testId="clock-my"
        label="My Time"
        icon={Briefcase}
        tz={localTz}
        date={now}
        accent="cyan"
        sublabel="Local · auto-detected"
        is12h={is12h}
        onToggleFormat={toggle}
      />
      <ClockCard
        testId="clock-client"
        label="Client Time"
        icon={MapPin}
        tz={active.clientTimezone}
        date={now}
        accent={clientOutside ? 'amber' : 'cyan'}
        sublabel={
          active.id
            ? active.hasExplicitTz
              ? `${active.row?.client ?? active.row?.name ?? active.id}`
              : 'No project tz — falling back to local'
            : 'No active project selected'
        }
        highlight={clientOutside}
        projectPicker={<ProjectPicker />}
        is12h={is12h}
        onToggleFormat={toggle}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Clock card
// ---------------------------------------------------------------------------
function ClockCard({
  testId, label, icon: Icon, tz, date, accent, sublabel,
  highlight, projectPicker, is12h, onToggleFormat,
}: {
  testId: string;
  label: string;
  icon: typeof Clock;
  tz: string;
  date: Date;
  accent: 'cyan' | 'amber';
  sublabel: string;
  highlight?: boolean;
  projectPicker?: React.ReactNode;
  is12h: boolean;
  onToggleFormat: () => void;
}): JSX.Element {
  const [flash, setFlash] = useState(false);

  const fullTime = TimezoneService.formatTimeInZone(date, tz, { withSeconds: true, hour12: is12h });
  const dateLine = TimezoneService.formatDateInZone(date, tz);
  const abbr     = TimezoneService.tzAbbreviation(tz, date);
  const offset   = TimezoneService.utcOffset(tz, date);

  // Split "HH:MM:SS AM" → ["HH:MM:SS", "AM"] for styled display
  const parts  = fullTime.split(' ');
  const timeStr = parts[0] ?? fullTime;
  const ampm    = is12h ? (parts[1] ?? '') : '';

  const handleTimeClick = () => {
    onToggleFormat();
    setFlash(true);
    setTimeout(() => setFlash(false), 400);
  };

  const tone =
    accent === 'amber'
      ? {
          border:    'border-amber-400/40',
          ring:      'shadow-[0_0_22px_rgba(251,191,36,0.18)]',
          text:      'text-amber-200',
          accentDot: 'bg-amber-300',
          label:     'text-amber-300/80',
          mono:      'text-amber-100',
          glow:      '0 0 14px rgba(251,191,36,0.35)',
        }
      : {
          border:    'border-cyan-400/35',
          ring:      'shadow-[0_0_22px_rgba(34,211,238,0.14)]',
          text:      'text-cyan-200',
          accentDot: 'bg-cyan-300',
          label:     'text-cyan-300/80',
          mono:      'text-cyan-100',
          glow:      '0 0 14px rgba(34,211,238,0.30)',
        };

  return (
    <div
      data-testid={testId}
      className={
        `relative rounded-sm border ${tone.border} ${tone.ring} bg-zinc-950/70 ` +
        'px-4 py-3 overflow-hidden'
      }
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${tone.text}`} strokeWidth={1.75} />
        <span className={`text-[10px] uppercase tracking-[0.22em] ${tone.label}`}>{label}</span>
        {highlight && (
          <span
            data-testid={`${testId}-outside-hours`}
            className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] px-1.5 py-px rounded-sm bg-amber-500/15 text-amber-300 border border-amber-400/30"
          >
            <AlertTriangle className="h-2.5 w-2.5" /> Outside hours
          </span>
        )}
      </div>

      {/* ── Clickable time display ── */}
      <button
        type="button"
        data-testid={`${testId}-time-display`}
        onClick={handleTimeClick}
        title="Click to toggle 12h / 24h format"
        className={[
          'flex items-baseline gap-1.5 cursor-pointer group rounded-sm transition-opacity',
          flash ? 'opacity-60' : 'opacity-100',
        ].join(' ')}
      >
        <span
          className={`font-mono tabular-nums text-[40px] leading-none ${tone.mono}`}
          style={{ textShadow: tone.glow }}
        >
          {timeStr}
        </span>
        {ampm && (
          <span
            className={`font-mono tabular-nums text-[18px] leading-none ${tone.mono} opacity-70 group-hover:opacity-100 transition-opacity`}
            style={{ textShadow: tone.glow }}
          >
            {ampm}
          </span>
        )}
      </button>
      {/* Subtle hint */}
      <div className="text-[8.5px] text-zinc-600 uppercase tracking-[0.18em] mt-0.5 select-none">
        click to toggle format
      </div>

      <div className="mt-1 flex items-center gap-2 text-[10.5px] text-zinc-400 font-mono">
        <span className="tracking-wide">{dateLine}</span>
        <span className={`inline-block w-1 h-1 rounded-full ${tone.accentDot}`} />
        <span>{tz}</span>
        <span className={tone.label}>· {abbr}</span>
        <span className="text-zinc-600">{offset}</span>
      </div>

      <div className="mt-1.5 text-[10px] text-zinc-500 truncate">{sublabel}</div>

      {projectPicker && <div className="mt-2">{projectPicker}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active project picker
// ---------------------------------------------------------------------------
function ProjectPicker(): JSX.Element {
  const active = useActiveProject();
  if (active.isLoading) {
    return <div className="text-[10px] text-zinc-500">Loading projects…</div>;
  }
  return (
    <label className="block">
      <span className="block text-[9.5px] uppercase tracking-[0.18em] text-zinc-500 mb-0.5">Active Project</span>
      <select
        data-testid="active-project-picker"
        value={active.id ?? ''}
        onChange={(e) => active.setActiveProject(e.target.value || null)}
        className="w-full bg-zinc-900/80 border border-zinc-700 rounded-sm text-zinc-100 text-[11px] px-2 py-1 focus:border-cyan-400/50 focus:outline-none"
      >
        <option value="">— No active project —</option>
        {active.available.map((p) => (
          <option key={p.id} value={p.id}>
            {p.projectNumber} · {p.client || p.name}
            {p.clientTimezone ? '' : '  (no tz)'}
          </option>
        ))}
      </select>
    </label>
  );
}
